import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, withFormik } from 'formik';
import compose from 'lodash/flowRight';
import * as Yup from 'yup';
import { setUserCity } from '@firsttable/functions';
import Form from '@firsttable/web-components/organisms/Forms/FormLayout';
import { Box, Divider } from '@firsttable/web-components/atoms';
import FormField from '@firsttable/web-components/molecules/FormField/FormField';
import { withRouter } from 'next/router';
import { BaseFormProps } from '@firsttable/types';
import Button from '@components/analytics/components/Button';
import withAnalytics from '@components/analytics/hocs/withAnalytics';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import withMouseDown from '@/components/analytics/hocs/withMouseDown';
import { PD6630_HOMEPAGE_RESTAURANTS_FLAG } from '@/components/analytics/experiments/PD6630HomepageRestaurants';
import useFeatureFlagValue from '@/components/analytics/hooks/useFeatureFlagValue';

const heights = {
  s: '54px',
  m: '90px',
  l: '110px',
};

const LinkWithTracking = withMouseDown(Box);

type CityOption = {
  value: string;
  label: string;
};

type CityNode = {
  menuTitle?: string;
  slug?: string;
};

const buildRegionOptions = (subCities: any): CityOption[] =>
  (subCities?.edges || [])
    .map(({ node }: { node: CityNode }) => ({
      value: node?.slug || '',
      label: node?.menuTitle || '',
    }))
    .filter((option: any) => !!option.value && !!option.label);

const citySearchFormDefaultSubCities = {};
const CitySearchForm = ({
  handleSubmit,
  subCities = citySearchFormDefaultSubCities,
  values,
  setFieldValue,
  isSubmitting,
}: BaseFormProps & {
  subCities: any;
  values?: { city?: string };
  setFieldValue?: (field: string, value: string) => void;
}) => {
  const findATableButtonCopy = useFeatureFlagValue(
    '099:find_a_table_button:home:copy:web',
    'Find a table'
  );
  const isPD6630 = useFeatureFlagValue(PD6630_HOMEPAGE_RESTAURANTS_FLAG, false) === true;

  const cityOptions = useMemo(() => buildRegionOptions(subCities), [subCities]);

  useEffect(() => {
    if (!setFieldValue) {
      return;
    }

    if (!values?.city) {
      if (cityOptions.length === 1) {
        setFieldValue('city', cityOptions[0].value);
      }
      return;
    }

    const selectedExists = cityOptions.some((option) => option.value === values.city);
    if (!selectedExists) {
      setFieldValue('city', cityOptions.length === 1 ? cityOptions[0].value : '');
    }
  }, [cityOptions, setFieldValue, values?.city]);

  return (
    <Form
      handleSubmit={handleSubmit}
      status={false}
      Action={null}
      isSubmitting={isSubmitting}
      isModal={false}
      actionLabel={null}>
      <Box
        bg={[null, '#fff']}
        borderRadius={2}
        display={['block', 'flex']}
        justifyContent="center"
        alignItems="center"
        width={['100%', 'fit-content']}
        mx="auto">
        <TrackProvider button_name="city picker" button_type="dropdown">
          <LinkWithTracking
            position="relative"
            width={['100%', '400px']}
            mb={[3, 0]}
            minWidth="200px">
            <Field
              component={FormField}
              name="city"
              type="select"
              placeholder="Select a Region"
              inlineError={false}
              pure
              styles={[
                { height: heights.s, padding: '0 10px 0 20px' },
                { height: heights.m, padding: '0 15px 0 30px' },
                { height: heights.l, padding: '0 25px 0 40px' },
              ]}
              options={cityOptions}
            />
          </LinkWithTracking>
        </TrackProvider>

        <Divider
          height={[0, heights.m, heights.l]}
          width={['100%', '1px']}
          style={{ flexShrink: 0 }}
        />

        <Box p={[0, '18px', '28px']} style={{ flexShrink: 0 }} width={[null, null, '240px']}>
          <TrackProvider button_name="find a table" button_type="cta">
            <Button
              wide
              kind={isPD6630 ? 'normal' : 'cta'}
              type="submit"
              isLoading={isSubmitting}
              data-testid="search__button"
              size="l">
              {findATableButtonCopy}
            </Button>
          </TrackProvider>
        </Box>
      </Box>
    </Form>
  );
};

CitySearchForm.propTypes = {
  initialValues: PropTypes.shape({
    city: PropTypes.string,
  }),
  subCities: PropTypes.shape({}),
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

const HomeSearchSchema = Yup.object().shape({
  city: Yup.string().required('Required'),
});

type FormValues = {
  city?: string;
};

export default compose(
  withAnalytics,
  withRouter,
  withFormik<any, FormValues>({
    enableReinitialize: true,
    validationSchema: HomeSearchSchema,
    mapPropsToValues: ({ initialValues, subCities }) => {
      const cityExists = subCities?.edges
        ? subCities.edges.filter(({ node }: any) => node?.slug === initialValues?.city).length
        : false;
      const city = cityExists ? initialValues?.city : '';
      return {
        city,
      };
    },
    handleSubmit: async (values: any, { setSubmitting, props: { router, track } }) => {
      const cityPath = values.city ? values.city : '';
      const navigateToSlugs = [cityPath];

      const cityName = cityPath
        .replace(/\//g, '')
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      setSubmitting(false);

      track('filter_applied', {
        filtered_date_time: 'any',
        filtered_session: 'dinner',
        filtered_suburbs: [],
        filtered_additional_options: [],
        filtered_city_name: cityName,
      });

      setUserCity(values.city);

      if (values.city.length) {
        router.push(navigateToSlugs.join('/'));
      }
    },
  })
)(CitySearchForm);
